import type { StateClassKey } from "@tdelivr/design-tokens";

import type { BoardRow, DetailField } from "./board.ts";

/**
 * What the model serving and features board does to the two listings it reads, away from any
 * rendering.
 *
 * Built to `16 WP15 Platform Machinery.dc.html`, page `ml`, against two operations published for
 * this one page:
 *
 *   - ML-02 `GET /v1/platform/ml-features` —
 *     [ADR 0067](../../../docs/adr/0067-a-features-freshness-is-published-per-feature-and-its-values-its-subjects-and-its-population-are-not.md),
 *     which is ML-02's **first collection read of any kind**: its other three operations are a
 *     register, an entity-value write and an entity-value read, and the last needs an `entityKey` —
 *     a driver, a rider or a zone — which is a subject a platform board has no way to name;
 *   - ML-01 `GET /v1/platform/model-deployments` —
 *     [ADR 0068](../../../docs/adr/0068-a-platform-scoped-model-deployment-listing-answers-a-count-and-publishes-no-model.md),
 *     which is ML-01's **first `GET` of any kind**: before it the stored deployment was readable
 *     only by performing an inference against it, which needs the deployment identifier a board
 *     exists to discover.
 *
 * ```
 * ml:['Model serving and features','ML-01 · ML-02',
 *   'For every feature, freshness is the field that matters.',
 *   [['Models live','6','done','serving'],['Stale features','2','caution','fallback in use'],
 *    ['Missing features','1','failed','fallback'],['Shadow tests','1','active','']],
 *   ['Feature','Freshness','Fallback'],
 *   [['eta.traffic_speed','8 s','—','done','fresh'],
 *    ['demand.zone_forecast','41 min','last good value','caution','stale · serving fallback'],
 *    ['driver.acceptance_rate','missing','platform average','failed','missing · serving fallback']],
 *   'info','Four-way: fresh, stale with fallback, missing with fallback, unavailable.']
 * ```
 *
 * ## Two listings, one grid, and ML-01 fills no row at all
 *
 * **All three of the design's columns are ML-02's and ML-01 contributes no column** — ADR 0068 §7
 * says so in those words, and §9 of ADR 0067 agrees from the other side. So the rows are the
 * features, and ML-01's listing is read for the one thing the design asks it for: a count.
 *
 * It fills no detail-panel line either, and that is the difference from
 * `event-catalogue.ts`'s composition rather than an omission here. **Nothing publishes a relation
 * between a feature and a deployment.** ML-02's rows are keyed `(tenantId, featureId)` and ML-01's
 * `(tenantId, deploymentId)`; a deployment's `inputFields` would be the only field that could name
 * a feature, and ADR 0068 §4 withholds it — it is the model's feature vector and, through
 * `validateInput`, the allowlist an inference must match. There is no name to match on even
 * loosely, so no join is attempted: a row asserting which model consumes a feature would be the
 * `Consumer`-column mistake in a new place.
 *
 * What ML-01 therefore answers is a sentence under the grid, which is where this console puts what
 * it must say and has no cell for. A failure on that listing degrades that sentence and keeps every
 * feature on screen — `ui/ModelServing.tsx` on why the two are awaited apart.
 *
 * ## The freshness state is published, and its grain is not the row's grain
 *
 * `navigation.ts` recorded that no contract publishes the designed four-way freshness state. **It
 * does, and it has since the service was built**: `ServedFeature.freshness` is
 * `fresh | stale_fallback | missing_fallback | unavailable`. ADR 0067's Context is the record of
 * that correction — the sweep that missed it searched for `feature.freshnessState`,
 * `feature.fallbackReason` and `feature.staleness`, and the field is named `freshness`.
 *
 * What is genuinely missing is the **grain**. That state is computed per `(feature, entityKey)`
 * from one stored row's `expiresAt`, and this board's row is per feature. ADR 0067 §2 derives what
 * the per-feature reading can honestly say, and `freshnessOf` below is that derivation and nothing
 * more: it is the **newest value's** state. A feature with ten thousand entities is not in one of
 * four states — three of its entities can be stale while the newest is fresh — so the board says so
 * under the grid, the way `platform-flags.ts`'s `flagScopeNote` says what `on for 2 of 5` counts.
 * A per-entity state presented as a per-feature one would be the fault, and it is not drawn.
 *
 * ML-02 publishes **no count of entities at all**, by decision and not by oversight: ADR 0067 §5 is
 * named for that withholding, because `count(*)` over the values of `driver.acceptance_rate` is
 * that tenant's driver count and over a rider feature is its customer count. So there is no
 * fresh/stale tally per feature and no "values held" column, and this board does not ask for one.
 *
 * ## What the two listings publish, and what they deliberately do not
 *
 * ML-02's seven: `tenantId`, `featureId`, `name`, `version`, `maximumStalenessSeconds`,
 * `hasFallback`, `newestExpiresAt`. Withheld, each for ADR 0067 §4's reason: **every feature
 * value** — derived personal data the service classifies `internal | confidential | restricted`
 * itself; **every entity key** — the subject a value is about, whose keys without their values
 * still publish the population; **`sourceEventId`** — a join key back into another tenant's
 * operational events; **`fallbackValue` itself** — a model's default prediction, which is both ML
 * intellectual property and one operator's business constant; **`entityType`** and **`dataType`** —
 * what another operator models and how; **`privacyClassification`**; **`ownerSubject`**; and
 * **`createdAt`** — when an operator started modelling a feature is a timeline of its ML
 * programme.
 *
 * ML-01's five: `tenantId`, `deploymentId`, `active`, `version`, `updatedAt`. Withheld, each for
 * ADR 0068 §4's reason: **`modelReference`** and **`modelVersion`** — which model an operator has
 * deployed and at which version; **`inputFields`** — the feature vector and the inference
 * allowlist; **`endpointKey`** — the nearest thing in that service to a credential;
 * **`timeoutMilliseconds`** — one tenant's negotiated latency budget; **`fallbackCode`** — an
 * operational tell about its reliability posture.
 *
 * Only one of those absences costs this board a cell, and it is the one below.
 *
 * ## The cells nothing can fill
 *
 * **`Fallback`, which is one of the grid's three columns.** The design writes `—`,
 * `last good value` and `platform average`, and its own form offers four named strategies —
 * `last good value | platform average | model default | none (unavailable)`. **ML-02 models one
 * literal and `null`.** There is no strategy field to read, so this is unmodelled rather than
 * unpublished, and ADR 0067's closing section says a public read cannot close it. Worse for this
 * column, `last good value` is a *behaviour* — serve the expired value — and `serveValue` does the
 * opposite: it replaces the stale value with `fallbackValue`. So the strategy names are not even
 * descriptions of what this service does.
 *
 * What is published is the literal's **presence**, as `hasFallback`, and that presence is already
 * on screen in the design's own words: the badge reads `stale · serving fallback` where a fallback
 * is held and `unavailable` where none is. So the column draws the design's own `—` with the reason
 * on it and the panel carries the boolean, rather than a near-miss word in a cell headed for a
 * strategy. Filling it from `hasFallback` would put `configured` under a column whose three sample
 * values are three different strategies — a wrong answer where a missing one is available.
 *
 * **`Shadow tests · 1`, which is one of the four tiles.** `ApprovedDeployment` has no traffic
 * split, no shadow flag and no comparison target, and `shadow` and `canary` appear nowhere under
 * `services/machine-learning/` — ADR 0068's Context measured it. Unmodelled, not unpublished.
 *
 * **The KPI tile row, which is not drawn on any board of this console.** `Models live · 6`,
 * `Stale features · 2` and `Missing features · 1` are all computable from the pages in hand, and
 * two of them are tallies of features that `boardTally` could take straight off the rows — but no
 * listing on this platform publishes a total, so a tile reading `6` under a label meaning `6` would
 * be `6 so far`. Q-178 carries the tile row across this console; ADR 0068 §7 is the argument for
 * publishing `active` anyway, and the count it answers is said as a sentence under the grid where
 * the floor can be stated.
 *
 * **`eta.traffic_speed` is a dotted name and `featureId` is a URN containing it.** ADR 0067's
 * closing section declines to resolve one against the other and says the board draws whichever the
 * design draws. The design draws the dotted name, which is `name`; the URN is in the panel.
 */

/**
 * The scope ML-02's platform feature listing is bought with.
 *
 * A literal in this application's source, which is what grants it: `channel-scopes.mjs` derives
 * `tdelivr-platform-console`'s realm grant from the scope strings this console's own files name, so
 * naming it here is the grant and nothing else is. ADR 0032's mechanism, ADR 0049's rule that a
 * channel names only the scopes its drawn screens spend, and ADR 0067 §3 on why
 * `feature-serving:read` is not widened instead — it answers a named subject's feature value to its
 * holders today, and granting that to a browser in order to draw three columns is ADR 0025's
 * rejected alternative re-taken.
 *
 * **Not `feature-management:platform:read`**, which is CFG-02's and is already held by this console
 * for the `pflags` board. The two services use the same word for different things, which is the same
 * collision ADR 0067 §1 settles on the path.
 *
 * It is declared here rather than in `navigation.ts`, where this console's other eight
 * cross-boundary scopes are collected, because that file's page record for `ml` is wired centrally
 * and is not this change's to edit. Moving the two constants there once the page is routed would
 * keep "what can this console reach" answerable from one file, which is what that collection is
 * for.
 *
 * The literal lives in `navigation.ts`, where all eighteen of this console's cross-boundary scopes
 * are collected so that "what can this console reach" is answerable from one file. Re-exported here
 * so a reader of this module finds it beside the reasoning, and so `channel-scopes.mjs` sees one
 * literal rather than two.
 *
 * **Q-139 takes a further occasion here.** Nobody has decided who may hold a scope that reads across
 * every tenant on the platform, and this board names two more of them.
 */
export { ML_FEATURES_SCOPE } from "./navigation.ts";

/**
 * The scope ML-01's platform deployment listing is bought with.
 *
 * ADR 0068 §2. ML-01 has **no existing `:read` scope to widen**, which is a reason to name this one
 * carefully rather than a reason to reuse a write: `model-serving:configure` would grant a browser
 * the power to change what a tenant serves in order to count rows, and `model-serving:invoke` is
 * worse still, because it buys predictions.
 *
 * Collected in `navigation.ts` beside `ML_FEATURES_SCOPE` and re-exported here.
 */
export { MODEL_DEPLOYMENTS_SCOPE } from "./navigation.ts";

/**
 * One feature, as ML-02's platform listing publishes it.
 *
 * The seven fields of `PlatformMlFeatureSummary`, restated here rather than imported from the
 * generated client for one reason: the generated **client method** types `newestExpiresAt` and
 * `nextAfter` as `unknown`, because the contract writes their nullability as `type: [string,
 * "null"]` and the generator does not narrow that form. `ui/ModelServing.tsx` narrows both by
 * `typeof` at the edge, and everything below this line works on a type that says what the contract
 * says.
 */
export interface MlFeature {
  readonly tenantId: string;
  readonly featureId: string;
  readonly name: string;
  readonly version: number;
  readonly maximumStalenessSeconds: number;
  readonly hasFallback: boolean;
  /** The newest expiry across every entity's value, or `null` when the feature holds none. */
  readonly newestExpiresAt: string | null;
}

/** One deployment, as ML-01's platform listing publishes it. Five fields, and nothing about a model. */
export interface ModelDeployment {
  readonly tenantId: string;
  readonly deploymentId: string;
  readonly active: boolean;
  readonly version: number;
  readonly updatedAt: string;
}

/** The four-way state `serveValue` computes, lifted from one entity to the newest. */
export type Freshness = "fresh" | "stale_fallback" | "missing_fallback" | "unavailable";

/**
 * The four-way state, derived from two published fields exactly as ADR 0067 §2 derives it.
 *
 * ```
 * newestExpiresAt === null → missing_fallback if hasFallback, else unavailable
 * newestExpiresAt > now    → fresh
 * otherwise                → stale_fallback if hasFallback, else unavailable
 * ```
 *
 * That is `serveValue`'s own logic with one substitution: where it reads one entity's `expiresAt`,
 * this reads the maximum across the feature's entities. So it is **the newest value's state**,
 * which is the honest per-feature reading of a per-entity model and is the reading the design's row
 * implies. The board says so under the grid; see this file's header.
 *
 * `tests/security/platform-listing.test.ts` in ML-02 asserts the two fields for each of
 * `serveValue`'s three reachable branches, so this derivation is measured against the same data the
 * service's own function would see.
 */
export function freshnessOf(feature: MlFeature, now: number): Freshness {
  if (feature.newestExpiresAt === null) {
    return feature.hasFallback ? "missing_fallback" : "unavailable";
  }
  const expires = Date.parse(feature.newestExpiresAt);
  // An unparseable instant is treated as no instant rather than as an expired one: the contract
  // types it `date-time`, so this is a row the platform wrote wrong, and reading it as stale would
  // badge a healthy feature from a malformed string.
  if (!Number.isFinite(expires)) {
    return feature.hasFallback ? "missing_fallback" : "unavailable";
  }
  if (expires > now) return "fresh";
  return feature.hasFallback ? "stale_fallback" : "unavailable";
}

/**
 * A number of seconds in the design's own duration grammar: `8 s`, `41 min`, `3 h`.
 *
 * All three forms are this design file's: `8 s` and `41 min` are the `ml` page's own `Freshness`
 * cells, and `3 h` is the `dlq` page's `Oldest` tile. The unit is the largest one whose number is
 * at least one, and the number is whole — the design writes no decimals and no compound form.
 *
 * Days are this console's extension for a staleness limit a tenant may legitimately set in days;
 * `maximumStalenessSeconds` has no published upper bound, so a limit of a week would otherwise read
 * `168 h`.
 */
export function durationText(seconds: number): string {
  const whole = Math.max(0, Math.round(seconds));
  if (whole < 60) return `${String(whole)} s`;
  if (whole < 3_600) return `${String(Math.round(whole / 60))} min`;
  if (whole < 172_800) return `${String(Math.round(whole / 3_600))} h`;
  return `${String(Math.round(whole / 86_400))} d`;
}

/**
 * The design's `Freshness` column: `8 s`, `41 min`, `missing`.
 *
 * How old the newest value is, which the design writes as an age rather than as an instant. It is
 * **exact rather than approximate**, because `expiresAt = observedAt + maximumStalenessSeconds` by
 * construction in `FeatureServingService.put` — so `now - (newestExpiresAt -
 * maximumStalenessSeconds)` is `now - observedAt` without `observedAt` being published at all. ADR
 * 0067 §2, and §8 is why the published instant is the expiry rather than the observation:
 * `observed_at` is in no index, so `max(observed_at)` would scan every value row on the platform.
 *
 * `missing` where the feature holds no value, which is the design's own word for that cell on its
 * third row rather than one invented here.
 */
export function freshnessText(feature: MlFeature, now: number): string {
  if (feature.newestExpiresAt === null) return "missing";
  const expires = Date.parse(feature.newestExpiresAt);
  if (!Number.isFinite(expires)) return "missing";
  const observed = expires - feature.maximumStalenessSeconds * 1000;
  return durationText((now - observed) / 1000);
}

/**
 * The badge's class and word, from the derived four-way state.
 *
 * Three of the four words are the design's own rows, word for word including the middle dot. The
 * fourth has no designed row and takes the word the design's own closing note gives it —
 * "Four-way: fresh, stale with fallback, missing with fallback, **unavailable**" — which is also
 * ML-02's own enum value, so the two agree.
 *
 * | derived            | class     | word                          | whose word                     |
 * | ------------------ | --------- | ----------------------------- | ------------------------------ |
 * | `fresh`            | `done`    | `fresh`                       | the design's first row         |
 * | `stale_fallback`   | `caution` | `stale · serving fallback`    | the design's second row        |
 * | `missing_fallback` | `failed`  | `missing · serving fallback`  | the design's third row         |
 * | `unavailable`      | `failed`  | `unavailable`                 | the design's note, and ML-02's |
 *
 * `unavailable` is `failed` because it is the worse half of the state the design already draws
 * `failed`: a feature with no value **and** no fallback tells a model nothing at all, where
 * `missing · serving fallback` at least serves a default. The two carry different words, so a
 * reader who cannot tell the badges apart by colour still reads which one it is — a state is never
 * shown by colour alone.
 */
export function stateOf(
  feature: MlFeature,
  now: number,
): { classKey: StateClassKey; label: string; title?: string } {
  switch (freshnessOf(feature, now)) {
    case "fresh":
      return {
        classKey: "done",
        label: "fresh",
        title:
          "The newest value across this feature's entities has not expired. This is the newest value's state, not every entity's: ML-02 computes freshness per feature and entity, and publishes no count of entities at all. ADR 0067 §2 and §5.",
      };
    case "stale_fallback":
      return {
        classKey: "caution",
        label: "stale · serving fallback",
        title:
          "The newest value has expired and a fallback literal is configured, so serveValue answers the fallback. Which strategy that is has no source: ML-02 models one literal and null, and the design's four named strategies are unmodelled. ADR 0067 §10.",
      };
    case "missing_fallback":
      return {
        classKey: "failed",
        label: "missing · serving fallback",
        title:
          "This feature holds no value for any entity and a fallback literal is configured. The literal itself is withheld — a model's default prediction is ML intellectual property and one operator's business constant. ADR 0067 §4.",
      };
    case "unavailable":
      return {
        classKey: "failed",
        label: "unavailable",
        title:
          "No value and no fallback, so serveValue answers nothing. The design draws no row for this state; the word is its own closing note's and ML-02's enum value. ADR 0067 §2.",
      };
  }
}

/** The states this derivation can reach, in the order ML-02's own enum declares them. */
export const FRESHNESS_STATES: readonly string[] = [
  "fresh",
  "stale · serving fallback",
  "missing · serving fallback",
  "unavailable",
];

/** The states present on the rows the board is holding, so the filter offers nothing empty. */
export function statesPresent(features: readonly MlFeature[], now: number): readonly string[] {
  const present = new Set(features.map((feature) => stateOf(feature, now).label));
  return FRESHNESS_STATES.filter((label) => present.has(label));
}

/**
 * An instant as the design's two time grammars compose it: `1 Sep 2026 09:12`.
 *
 * Both halves are the design's — `1 Sep 2026` is every date cell in these files and `09:12` is the
 * `History` block's `Last changed` — and neither alone says what this panel line has to say. A day
 * alone would lose the time of a value whose whole subject is seconds of staleness; a clock alone
 * would not say which day a value expired on. The clock is 24-hour, as the design writes it, and
 * UTC, because every instant on these boards is published as UTC and the design names no zone
 * anywhere (Q-24).
 */
const clock = new Intl.DateTimeFormat("en-GB", {
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
  timeZone: "UTC",
});

export function asInstant(instant: string): string {
  const parsed = Date.parse(instant);
  if (!Number.isFinite(parsed)) return instant;
  const at = new Date(parsed);
  const month = new Intl.DateTimeFormat("en-GB", { month: "short", timeZone: "UTC" })
    .format(at)
    .slice(0, 3);
  return `${String(at.getUTCDate())} ${month} ${String(at.getUTCFullYear())} ${clock.format(at)}`;
}

/**
 * One row of the model serving and features board.
 *
 * @param feature one feature, as ML-02's platform listing answered it.
 * @param now the instant the freshness is read against. Passed rather than read, so a row is a
 *   function of its inputs and the fidelity harness's pinned clock reaches it.
 */
export function featureRow(feature: MlFeature, now: number): BoardRow {
  const state = stateOf(feature, now);
  const expiry: DetailField =
    feature.newestExpiresAt === null
      ? { label: "Newest value expires", value: "", absent: true }
      : { label: "Newest value expires", value: asInstant(feature.newestExpiresAt) };

  return {
    /*
     * The tenant and the feature: ML-02's primary key and this listing's cursor. Prefixed with the
     * tenant because the listing spans tenants and a `featureId` is unique only within one — ADR
     * 0067 §7 — so a React key that collided would make one tenant's row replace another's on a
     * board whose whole purpose is seeing every tenant's.
     */
    id: `${feature.tenantId}:${feature.featureId}`,
    cells: [
      {
        /*
         * `name`, which is the dotted string the design writes — `eta.traffic_speed`. The URN
         * containing it is in the panel. ADR 0067 declines to resolve one against the other and
         * says the board draws whichever the design draws.
         */
        text: feature.name,
        title: `Published as ${feature.featureId}. The design writes the dotted name, which is the published name field; the identifier is the URN containing it.`,
      },
      {
        text: freshnessText(feature, now),
        title:
          feature.newestExpiresAt === null
            ? "This feature holds no value for any entity, so there is no age to show. The design's own word for this cell is missing."
            : `How old the newest value across this feature's entities is, exact rather than approximate: expiresAt equals observedAt plus maximumStalenessSeconds by construction, so the age needs no observedAt. It is the newest value's age and not every entity's. ADR 0067 §2.`,
      },
      {
        text: "",
        absent: true,
        title:
          "The design's four fallback strategies — last good value, platform average, model default, none (unavailable) — are unmodelled: ML-02 holds one fallback literal and null, and the literal is withheld because a model's default prediction is ML intellectual property and one operator's business constant. Whether one is held at all is published, and the badge beside this row says so in the design's own words. ADR 0067 §4 and §10.",
      },
    ],
    state,
    /*
     * No row tint. The design tints its `human` and `failed` classes — `--humanRow` and `--failRow`
     * — and this console's stylesheet carries a rule for the first only, which no state on this
     * board can reach. Tinting a `failed` row with `--humanRow` would be both the wrong colour and
     * a false signal, since that tint means "a person is waited on" everywhere else here. The badge
     * carries the glyph and the word either way. `event-catalogue.ts` records the same absence.
     */
    detail: [
      { label: "Tenant", value: feature.tenantId, mono: true },
      { label: "Feature", value: feature.featureId, mono: true },
      /*
       * The design's own words for this field, taken from its own form: `Freshness limit`, whose
       * placeholder is `e.g. 60 s` and whose hint is "Older than this is stale". So the label, the
       * grammar and the meaning are all the design's; only the value is ML-02's.
       */
      { label: "Freshness limit", value: durationText(feature.maximumStalenessSeconds) },
      expiry,
      /*
       * `hasFallback` under the field's own name, rather than as a strategy word in the `Fallback`
       * column. The column is headed for one of four strategies and this is a presence; see this
       * file's header on why the two are not the same answer.
       */
      { label: "Has fallback", value: feature.hasFallback ? "yes" : "no" },
    ],
    /*
     * The design's `History` block with the one entry a feature definition genuinely has.
     *
     * That block reads `Created`, `Last changed` and `Version` on every page of every one of these
     * design files, with a person's name beside the first two. ML-02 publishes **neither instant**:
     * `createdAt` is withheld because when an operator started modelling a feature is a timeline of
     * its ML programme (ADR 0067 §4), and there is no `updatedAt` to withhold — `feature_definitions`
     * is written by one `INSERT … ON CONFLICT DO NOTHING`, which refuses a re-registration with 409
     * rather than changing a row, so nothing about a definition is ever last-changed. `ownerSubject`
     * is published to the service and withheld here, so the design's two names have no source in any
     * case. Q-107 is where the missing actor on every `History` block lives.
     */
    history: [
      { label: "Created", value: "", absent: true },
      { label: "Last changed", value: "", absent: true },
      { label: "Version", value: `v${String(feature.version)}` },
    ],
    /*
     * The design's own glyph for this panel, which is its `FORMS.ml` icon: `model_training`. Its
     * panel head renders `fm.icon`, and `fm.icon` comes from the page's entry in `FORMS`. The page's
     * rail glyph is the same string here, which is a coincidence of this page rather than a rule —
     * `dlq` has no `FORMS` entry at all and draws `info`.
     */
    glyph: "model_training",
  };
}

/**
 * What the board owes an operator about the two listings behind it.
 *
 * Four things, and each is said rather than implied because each invites a stronger reading than the
 * data supports:
 *
 *   - **how many tenants the rows came from**, because the page is a board of every online feature
 *     on the platform and cannot show that it is: nothing publishes how many tenants exist —
 *     `POST /v1/tenants` exists and there is no `GET` — so "features from 3 tenants" is exactly
 *     true and "3 of 7 tenants" is unsayable. `platform-flags.ts`'s `flagScopeNote` and
 *     `partner-api.ts`'s `tenantSpanNote` are the same sentence for the same reason.
 *   - **what the `Freshness` column and the badge are the state of**, which ADR 0067 §2 requires a
 *     board drawing them to say: the newest value's, not every entity's. This is the sentence that
 *     record asks for, and it is the one thing on this page that could be read as a stronger claim
 *     without anything on screen looking wrong.
 *   - **that the `Fallback` column has no source**, which is a third of the grid and is the one
 *     absence a reader cannot discover by hovering a cell they did not think to hover.
 *   - **what ML-01 answered**, because it is named in the page's own source chip, fills no row, and
 *     an operator who never sees a number from it cannot tell whether it answered.
 *
 * @param features the features on the board, or `undefined` before they have arrived.
 * @param deployments the deployment page, or `undefined` when ML-01 did not answer.
 * @param deploymentsTruncated whether ML-01 answered a cursor, so the live count is a floor.
 */
export function listingNote(
  features: readonly MlFeature[] | undefined,
  deployments: readonly ModelDeployment[] | undefined,
  deploymentsTruncated: boolean,
): string | undefined {
  if (features === undefined) return undefined;
  const tenants = new Set(features.map((feature) => feature.tenantId)).size;
  if (tenants === 0) return undefined;

  const span = `Features from ${String(tenants)} tenant${tenants === 1 ? "" : "s"}.`;
  const grain =
    "Freshness is the newest value's, not every entity's, because the number of entities on a driver feature is that tenant's driver count.";

  if (deployments === undefined) {
    return `${span} ${grain} ML-01's deployment listing did not answer, so no count of live models is shown.`;
  }

  const live = deployments.filter((deployment) => deployment.active).length;
  const floor = deploymentsTruncated
    ? ` More than ${String(deployments.length)} deployments exist, so that is a floor rather than a total.`
    : "";
  return `${span} ${grain} ML-01 answers ${String(live)} live model deployment${live === 1 ? "" : "s"} of ${String(deployments.length)} across the platform.${floor}`;
}

/**
 * Why the designed `Register a feature` form is drawn and refused.
 *
 * Two reasons, and either is enough. The form's fields are `Name`, `Freshness limit` and
 * `Fallback`, and its primary is `Register`:
 *
 *   - **`Fallback` is required and offers four named strategies ML-02 does not model.** It holds one
 *     literal and `null`; `last good value` in particular is a behaviour, and `serveValue` does the
 *     opposite of it. A write would have to invent the field, which `CLAUDE.md` §2 forbids.
 *   - **No write is published under `/v1/platform` at all.** ADR 0067 §10, and ML-02's
 *     `tests/security/platform-listing.test.ts` asserts the router's 404 on three platform-shaped
 *     writes with the platform scope held — so "not published" is measured rather than described.
 *
 * The word on the button is the design's own `FORMS.ml[0]`: `Register feature`.
 */
export const REGISTER_REFUSAL =
  "Not available. The form's required Fallback field offers four named strategies ML-02 does not model — it holds one fallback literal and null — and no write is published under /v1/platform. ADR 0067 §10.";
